import { useAuthFetch } from "@p0security/frontend-core";
import {
  ErrorDisplay,
  UnknownFirestoreDoc,
} from "@p0security/frontend-core/react";
import { AnyNode } from "@p0security/shared/types/assessment/data/index";
import { Finding } from "@p0security/shared/types/assessment/finding";
import { Spin } from "@p0security/ui";
import { SelectedEnvironmentContext } from "components/Environment/contexts/SelectedEnvironmentContext";
import { useContext, useEffect, useMemo, useState } from "react";

import { ResultDetail } from "../components/ResultDetail";
import { FindingStateEditor } from "../components/finding/FindingStateEditor";
import { FindingsContext } from "../contexts/FindingsContext";
import { ScopeContext } from "../contexts/ScopeContext";
import { useControls } from "../hooks/useControls";

export const FindingDetail: React.FC<{
  finding: UnknownFirestoreDoc<Finding>;
  node?: AnyNode;
}> = ({ finding, node }) => {
  const [error, setError] = useState<string>();
  const [isFetching, setIsFetching] = useState(false);

  const authFetch = useAuthFetch(setError, setIsFetching);
  const { allMonitors } = useContext(FindingsContext);
  const { last } = useContext(SelectedEnvironmentContext);
  const { graph, step, scopeKey, setScopeKey } = useContext(ScopeContext);
  const { controls } = useControls();

  useEffect(() => {
    if (finding.doc && scopeKey && scopeKey !== finding.doc?.data.scopeKey) {
      setScopeKey(finding.doc.data.scopeKey, "replaceIn");
    }
  }, [finding.doc, scopeKey, setScopeKey]);

  const monitor = useMemo(
    () =>
      finding.doc?.data.monitorId
        ? allMonitors[finding.doc.data.monitorId]
        : undefined,
    [finding.doc, allMonitors]
  );

  const terms = useMemo(() => {
    const monitorTerm = monitor?.searchTerm ?? "";
    return [monitorTerm, controls.where.trim()].join(" ").trim();
  }, [monitor?.searchTerm, controls.where]);

  return !finding.loading &&
    !last.loading &&
    step === "aggregating" &&
    finding.doc &&
    graph &&
    monitor ? (
    <ResultDetail
      header={
        <div>
          {error && (
            <ErrorDisplay title="Could not update finding" error={error} />
          )}
          {isFetching ? (
            <Spin />
          ) : (
            <FindingStateEditor
              node={node}
              finding={finding.doc}
              authFetch={authFetch}
            />
          )}
        </div>
      }
      data-testid="finding-detail"
      mode={"finding"}
      node={finding.doc.data.node}
      title={
        <>
          Finding {finding.doc.data.humanId} &ndash; {monitor?.label}
        </>
      }
      terms={terms}
    />
  ) : null;
};
